import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense, useState, useEffect } from 'react';
import { ThemeProvider } from './contexts/ThemeContext';
import { DateFilterProvider } from './contexts/DateFilterContext';
import { AuthProvider } from './auth/AuthProvider';
import { PrivateRoute } from './auth/PrivateRoute';
import { Layout } from './components/Layout';
import { NotFound } from './components/NotFound';
import { Login } from './pages/Login';
import { AuthCallback } from './pages/AuthCallback';
import { fetchRuns } from './api/client';
import type { Run } from './types';

// Pages load on demand so the first paint only downloads the shell.
// Dashboard serves the new view by default; REACT_APP_DASHBOARD_VIEW=classic (build time) serves the original.
const Dashboard        = lazy(() => import('./pages/AllRunsV2').then(m => ({ default: m.Dashboard })));
const RunDetail        = lazy(() => import('./pages/RunDetail').then(m => ({ default: m.RunDetail })));
const TestCaseDetail   = lazy(() => import('./pages/TestCaseDetail').then(m => ({ default: m.TestCaseDetail })));
const TestCaseExplorer = lazy(() => import('./pages/TestCaseExplorer').then(m => ({ default: m.TestCaseExplorer })));
const Bugs             = lazy(() => import('./pages/Bugs').then(m => ({ default: m.Bugs })));
const NewRun           = lazy(() => import('./pages/NewRun').then(m => ({ default: m.NewRun })));

function PageFallback() {
  return (
    <div className="page-enter" style={{ paddingTop: '28px' }}>
      <div className="shimmer" style={{ height: '20px', width: '200px', borderRadius: '4px', marginBottom: '24px' }} />
      <div className="shimmer" style={{ height: '120px', borderRadius: '10px' }} />
    </div>
  );
}

function AppRoutes() {
  const [allRuns, setAllRuns] = useState<Run[]>([]);

  useEffect(() => {
    fetchRuns().then(setAllRuns).catch((e) => console.error('[expo] fetchRuns failed:', e));
  }, []);

  return (
    <DateFilterProvider allRuns={allRuns}>
      <Routes>
        {/* Public */}
        <Route path="/login"         element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Private */}
        <Route
          path="/*"
          element={
            <PrivateRoute>
              <Layout>
                <Suspense fallback={<PageFallback />}>
                <Routes>
                  <Route path="/runs"                    element={<Dashboard />} />
                  <Route path="/runs/:runId"             element={<RunDetail />} />
                  <Route path="/test-cases"              element={<TestCaseExplorer />} />
                  <Route path="/test-cases/:runId/:id"   element={<TestCaseDetail />} />
                  <Route path="/bugs"                    element={<Bugs />} />
                  <Route path="/new-run"                 element={<NewRun />} />
                  <Route path="/"                        element={<Navigate to="/runs" replace />} />
                  <Route path="*"                        element={<NotFound what="Page" to="/runs" label="Dashboard" />} />
                </Routes>
                </Suspense>
              </Layout>
            </PrivateRoute>
          }
        />
      </Routes>
    </DateFilterProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
